import Layout from '../../components/layout/Layout'
import ExerciseCard from '@/components/ui/exercise-card'
import chibatoreData from '@/data/thoughts/chibatore.json'
import { ASSETS } from '@/constants/assets'
import ImageCard from '@/components/ui/image-card'
import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HandDrawnHighlight } from '../../components/HandDrawnHighlight'

const ConceptCollapsible = ({ 
  title, 
  children, 
  isOpen, 
  onToggle 
}: { 
  title: string
  children: React.ReactNode
  isOpen: boolean
  onToggle: () => void 
}) => {
  return (
    <div>
      <div 
        className="flex items-center cursor-pointer gap-2" 
        onClick={onToggle}
      >
        <h3 className="text-xl font-semibold">{title}</h3>
        <ChevronDown 
          className={cn(
            "w-4 h-4 transition-transform duration-200",
            isOpen ? "transform rotate-180" : ""
          )} 
        />
      </div>
      <div
        className={cn(
          "grid transition-all duration-200 ease-in-out mt-3",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  )
}

const CollapsibleSection = ({ 
  title, 
  children, 
  isOpen, 
  onToggle 
}: { 
  title: string
  children: React.ReactNode
  isOpen: boolean
  onToggle: () => void 
}) => {
  return (
    <div className="border-b border-foreground/10">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-[1.953rem] font-semibold text-foreground font-sans tracking-[-0.025em] leading-[1.2] [text-wrap:balance]"
      >
        <span>{title}</span>
        <ChevronDown 
          className={cn(
            "w-6 h-6 transition-transform duration-200",
            isOpen ? "transform rotate-180" : ""
          )} 
        />
      </button>
      <div
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="py-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

const ThoughtChibatore = () => {
  const [openSections, setOpenSections] = useState({
    what: true,
    expansion: false,
    importance: false,
    journey: false,
    stateOfArt: false,
    concepts: true,
    inside: false,
    outside: false
  })

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const [showModal, setShowModal] = useState(false)
  const bridgeExercise = chibatoreData.exercises.find(ex => ex.title === "Arch & Bridge")

  const handleBridgeClick = () => {
    setShowModal(true)
    document.body.style.overflow = 'hidden'
  }

  const closeModal = () => {
    setShowModal(false)
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    if (showModal) window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [showModal])

  return (
    <Layout>
      <div className="w-full max-w-4xl px-4 md:px-8 lg:px-12 py-12 space-y-6">
        <h1 className="text-[2.488rem] font-semibold text-foreground font-sans tracking-[-0.025em] leading-[1.2] [text-wrap:balance]">
          Chibatore
        </h1>
        
        <p className="text-foreground/60 text-[1.375rem] leading-[1.3] [text-wrap:balance]">
          High-quality body movement to improve your health.
        </p>

        <div className="blog-body space-y-4 text-foreground text-[1.125rem] leading-[1.625] [text-wrap:balance]">
          <CollapsibleSection 
            title="What is Chibatore?" 
            isOpen={openSections.what}
            onToggle={() => toggleSection('what')}
          >
            <p>
              Chibatore is a Japanese movement system created by <a href="https://www.instagram.com/chibatore/?hl=en" target="_blank" rel="noopener noreferrer" className="text-foreground underline hover:text-foreground/80">Hiroshi Chiba (千葉 啓史)</a> that emphasizes integrated stength, motion, flexibility, and nervous system activation. It's designed to keep the body moving as naturally and efficiently as possible.
            </p>
            <br />
            <p>Chiba-San privately trains world champions like <a href="https://www.instagram.com/tomoa_narasaki/?hl=en" target="_blank" rel="noopener noreferrer" className="text-foreground underline hover:text-foreground/80">Tomoa Narasaki</a> and <a href="https://www.youtube.com/watch?v=A4w6Yz-QyWA" target="_blank" rel="noopener noreferrer" className="text-foreground underline hover:text-foreground/80">Akiyo Noguchi</a></p>

           {/* Instagram Embed */}
          <div className="my-8 flex justify-center">
            <iframe
              src="https://www.instagram.com/p/DEed-T4y--l/embed"
              width="400"
              height="711"
              frameBorder="0"
              scrolling="no"
              className="instagram-media rounded-lg shadow-lg max-w-full"
            ></iframe>
          </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Expansion of Choice" 
            isOpen={openSections.expansion}
            onToggle={() => toggleSection('expansion')}
          >
            <p>Chibatore's core goal is to expand your range of options in difficult situations—whether that's slipping on the stairs, lifting a heavy suitcase into an overhead bin, or recovering from a sudden misstep. Instead of relying on the same tired movement patterns, you'll have more "tools in your toolbox." You can choose to use them or not, but at least you'll have them when it counts.</p>
            <div className="w-[50%] mx-auto mt-4">
              <ImageCard 
                src={ASSETS.images.chibatoreOptions}
                alt="Chibatore Options"
                isSquare
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Why It Matters?" 
            isOpen={openSections.importance}
            onToggle={() => toggleSection('importance')}
          >
            <ul className="list-disc pl-6 space-y-2">
              <li><span className="font-semibold">Aging:</span> It engages the nervous system in a way that helps older individuals move more like their younger selves.</li>
              <li><span className="font-semibold">Chronic Pain Prevention:</span> By improving strength, flexibility, and coordination, it reduces stress on joints and muscles.</li>
              <li><span className="font-semibold">Chronic Pain Recovery:</span> It corrects harmful movement patterns and replaces them with healthier ones, promoting faster recovery.</li>
            </ul>
          </CollapsibleSection>

          <CollapsibleSection 
            title="My Journey" 
            isOpen={openSections.journey}
            onToggle={() => toggleSection('journey')}
          >
            <div className="space-y-4">
              <p>
                After knee surgery 4 years ago, I set out to explore the human body's highest movement potential. Meeting <a href="https://www.ukclimbing.com/news/olympics/2020/athletes/aleksei-rubtsov-9046" target="_blank" rel="noopener noreferrer" className="text-foreground underline hover:text-foreground/80">Alexey Rubtsov</a> (another multiple-world champion and oldest climber qualified for the Olympics) sparked my journey to Japan, where I spent most of 2024 studying Chibatore to ensure I never get injured again.
              </p>
              <p>
                I've built complex software systems my entire adult life. Yet, this movement method is the most complex knowledge I've encountered. You must first understand it intellectually and then physically integrate it. Reading about it is like reading about riding a bike—useless unless you actually get on and ride.
              </p>
              <p>I'm not a professional athlete, just an engineer who found this system fascinating—and life-changing. I've tried to structure its core ideas in a way anyone can understand. Think of it as a "movement philosophy" for the Western world.</p>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Inspirational examples" 
            isOpen={openSections.stateOfArt}
            onToggle={() => toggleSection('stateOfArt')}
          >
            <div className="space-y-4">
              <p>
                Here are the two examples of world class athletes demonstrating what human body movement is capable of.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                {chibatoreData.inspiration.map((card, index) => (
                  <ExerciseCard
                    key={index}
                    title={card.title}
                    description={card.description}
                    variations={[card.url]}
                    className="h-full"
                  />
                ))}
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Concepts" 
            isOpen={openSections.concepts}
            onToggle={() => toggleSection('concepts')}
          >
            <div className="space-y-4">
              <p>
                The entire Chibatore system can be represented in the following hierarchy. 
              </p>
              <p>Chibatore is divided into two key aspects — <b>Outside (外)</b> and <b>Inside (内)</b>. "Inside" primarely focuses on skills that our body needs to acquire through our mind. By learning these skills, we will be moving more efficiently and precisely. <b>"The Outside (外)"</b>  covers everything else. This is all familiar to the Western world - focus on high quality diet, recovery, stress control and body conditioning.</p>
              <br />
              I will carefully explain and demonstrate each concept in the video cards below. See the Bridge example in this hierarchy. 
              
              <div className="space-y-6">
                <ConceptCollapsible
                  title="Inside 内"
                  isOpen={openSections.inside}
                  onToggle={() => toggleSection('inside')}
                >
                  <div className="pl-6 space-y-4">
                    <div>
                      <h4 className="text-lg font-medium mb-2">Skills スキル</h4>
                      <div className="pl-6 space-y-4">
                        <div>
                          <h5 className="font-medium mb-2">Non-Use of Power 脱力</h5>
                          <div className="pl-6 space-y-3">
                            <div>
                              <span className="font-medium">Concept 1</span>
                              <ul className="pl-6 list-disc">
                                <li>Attraction 引力</li>
                                <li>Body Segmentation 割体</li>
                              </ul>
                            </div>
                            <div>
                              <span className="font-medium">Concept 2</span>
                              <ul className="pl-6 list-disc">
                                <li>Circular power 円心力</li>
                                <li>Swing + Pendulum 振動</li>
                              </ul>
                            </div>
                            <div>
                              <span className="font-medium">Concept 3</span>
                              <ul className="pl-6 list-disc">
                                <li>Ascent (Buoyancy) 浮上</li>
                                <li>Descent 落下</li>
                                <li>Vector of force ベクトル</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium mb-2">Use of Power 出力</h5>
                          <div className="pl-6 space-y-3">
                            <div>
                              <span className="font-medium">Concept 1</span>
                              <ul className="pl-6 list-disc">
                                <li>Pull + Push プルプッシュ</li>
                                <li>Push プッシュ力</li>
                                <li>Pull プル力</li>
                              </ul>
                            </div>
                            <div>
                              <span className="font-medium">Speed 速</span>
                              <ul className="pl-6 list-disc">
                                <li>Acceleration 加速</li>
                                <li>Deceleration 減速</li>
                              </ul>
                            </div>
                            <ul className="pl-6 list-disc">
                              <li>Use of external/internal power 内力・外発</li>
                              <li>Contraction コントラクション</li>
                              <li>Retention of power 保持力 (Isolating the body prevents fatigue)</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ConceptCollapsible>

                <ConceptCollapsible
                  title="Outside 外"
                  isOpen={openSections.outside}
                  onToggle={() => toggleSection('outside')}
                >
                  <div className="pl-6 space-y-2">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Conditioning コンディションイング</li>
                      <li>Nutrition ニュートリション</li>
                      <li>Mental メンタル</li>
                      <li>Recovery リハビリ</li>
                      <li>
                        Physical フィジカル
                        <ul className="pl-6 list-disc mt-2 space-y-2">
                          <li>Hanging ハンギング</li>
                          <li>
                            <Link 
                              to="#"
                              className="inline-block border-b border-foreground pb-[2px]"
                              onClick={(e) => {
                                e.preventDefault()
                                handleBridgeClick()
                              }}
                            >
                              <HandDrawnHighlight delay={0}>Bridge ブリッジ</HandDrawnHighlight>
                            </Link>
                          </li>
                          <li>Stretching ストレッチ</li>
                          <li>Core Activation コアアクティベーション</li>
                          <li>Legs + Arms 手足</li>
                          <li>Shoulder ショルダー</li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                </ConceptCollapsible>
              </div>
            </div>
          </CollapsibleSection>
        </div>

        <h2 className="text-[1.953rem] font-semibold text-foreground font-sans tracking-[-0.025em] leading-[1.2] [text-wrap:balance] mb-8">
          The Key Exercises
        </h2>
        <br />
        <time className="text-muted-foreground text-[1rem] leading-normal">
          ** Last updated: January 27, 2025
        </time>
        <p>First <b>50 videos</b> are organized into <b>8 cards</b> by key categories:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Spine & Core</li>
          <li>Arch & Bridge</li>
          <li>Rolling Patterns</li>
          <li>Hand Balancing</li>
          <li>Hip Mobility</li>
          <li>Shoulder Health</li>
          <li>Lower Body</li>
          <li>Flow Sequences</li>
         
        </ul>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {chibatoreData.exercises.map((exercise, index) => (
            <ExerciseCard
              key={index}
              {...exercise}
              className="h-full"
            />
          ))}
        </div>

        <p className="text-foreground/60 text-[1.0rem] leading-[1.3] [text-wrap:balance]">
          If you have any feedback, please email to me directly at <a href="mailto:sergey@tsibel.com" className="text-foreground underline hover:text-foreground/80">sergey@tsibel.com</a>
        </p>
      </div>

      {showModal && bridgeExercise && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeModal}
        >
          <div 
            className="w-full max-w-2xl relative flex items-center"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 text-white hover:text-white/80 transition-colors rounded-full bg-black/50 hover:bg-black/70 z-50"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
            
            <ExerciseCard
              {...bridgeExercise}
              className="w-full h-auto max-h-[calc(100vh-4rem)]"
            />
          </div>
        </div>
      )}
    </Layout>
  )
}

export default ThoughtChibatore 